import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  Alert,
  Grid,
  FormHelperText,
  InputAdornment,
  Switch,
  Slider,
  FormGroup,
  RadioGroup,
  Radio,
  FormLabel,
  Autocomplete,
  Stack,
  Paper,
  Divider,
  Tooltip,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import QuillEditor from './QuillEditor';
import HtmlDescription from './HtmlDescription';
import SearchableSelect from './SearchableSelect';
import CustomNumberInput from './CustomNumberInput';
import ChipInput from './ChipInput';
import DynamicVariantFields from './DynamicVariantFields';
import DynamicAttributeFields from './DynamicAttributeFields';
import ImageUploadDragDrop from './common/ImageUploadDragDrop';
import SingleVideoUpload from './common/SingleVideoUpload';

const DynamicFormBuilder = ({ 
  schema, 
  initialData = {}, 
  onChange = () => {}, 
  onSubmit = () => {},
  mode = 'preview', // 'preview' or 'edit'
  readOnly = false 
}) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [variants, setVariants] = useState([]);
  const [pricePreview, setPricePreview] = useState(null);

  // Sync variant prices with bulk pricing base price
  useEffect(() => {
    const pricingStrategy = formData['Pricing Strategy'] || formData['Pricing Mode'];
    const bulkTiers = formData['Bulk Pricing Tiers'] || formData['Range Price Rules'] || [];
    
    // Only sync if using bulk pricing mode and there are tiers
    if ((pricingStrategy === 'bulkPrice' || pricingStrategy === 'rangePrice') && bulkTiers.length > 0) {
      const baseTierPrice = parseFloat(bulkTiers[0]['Price (₦)']) || 0;
      
      // Check if any variant has a different price
      const needsUpdate = variants.some(v => {
        const variantPrice = parseFloat(v['Price'] || v.price || 0);
        return variantPrice !== baseTierPrice;
      });
      
      if (needsUpdate && baseTierPrice > 0) {
        // Update all variants to match the base tier price
        const updatedVariants = variants.map(variant => ({
          ...variant,
          Price: baseTierPrice,
          price: baseTierPrice
        }));
        
        setVariants(updatedVariants);
        console.log(`🔄 Auto-synced variant prices to base bulk price: ₦${baseTierPrice}`);
      }
    }
  }, [formData['Pricing Strategy'], formData['Pricing Mode'], formData['Bulk Pricing Tiers'], formData['Range Price Rules']]);

  // Close price preview when pricing strategy changes
  useEffect(() => {
    if (pricePreview) {
      setPricePreview(null);
      console.log('🔄 Closed price preview due to pricing mode change');
    }
  }, [formData['Pricing Strategy'], formData['Pricing Mode']]);

  // Initialize form data from schema
  useEffect(() => {
    if (schema) {
      const initialFormData = {};
      
      // Handle different schema structures (support both uppercase and lowercase keys)
      const sections = schema.Sections || schema.sections;
      const fields = schema.Fields || schema.fields;
      
      if (sections) {
        // New enhanced structure with sections
        sections.forEach(section => {
          const sectionFields = section.Fields || section.fields;
          if (sectionFields) {
            sectionFields.forEach(field => {
              const fieldName = field.Name || field.name;
              const fieldType = field.Type || field.type;
              initialFormData[fieldName] = field.default || getDefaultValue(fieldType);
            });
          }
        });
      } else if (fields) {
        // Legacy structure with main fields
        fields.forEach(field => {
          const fieldName = field.Name || field.name;
          const fieldType = field.Type || field.type;
          initialFormData[fieldName] = field.default || getDefaultValue(fieldType);
        });
      }
      
      // Handle any additional field arrays (legacy support)
      Object.keys(schema).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (Array.isArray(schema[key]) && lowerKey !== 'fields' && lowerKey !== 'sections') {
          schema[key].forEach(field => {
            const fieldName = field.Name || field.name;
            const fieldType = field.Type || field.type;
            if (fieldName && fieldType) {
              initialFormData[fieldName] = field.default || getDefaultValue(fieldType);
            }
          });
        }
      });
      
      setFormData(initialFormData);
      
      // Initialize variants with at least one default variant
      initializeDefaultVariant();
    }
  }, [schema]);

  // Initialize default variant
  const initializeDefaultVariant = () => {
    // Check for Variants in top-level schema (support both cases)
    let variantSchema = schema.Variants || schema.variants;
    
    // Check for Variants section within Sections array (support both cases)
    const sections = schema.Sections || schema.sections;
    if (!variantSchema && sections) {
      const variantSection = sections.find(section => 
        (section.Section || section.section || section.Name || section.name || '').toLowerCase() === 'variants'
      );
      if (variantSection) {
        const fields = variantSection.Fields || variantSection.fields;
        variantSchema = { Fields: fields };
      }
    }
    
    if (variantSchema && variantSchema.Fields) {
      const defaultVariant = {};
      variantSchema.Fields.forEach(field => {
        const fieldName = field.Name || field.name;
        const fieldType = field.Type || field.type;
        defaultVariant[fieldName] = field.default || getDefaultValue(fieldType);
      });
      setVariants([defaultVariant]);
    }
  };

  // Get default value based on field type
  const getDefaultValue = (type) => {
    switch (type) {
      case 'string':
      case 'text':
        return '';
      case 'number':
      case 'integer':
        return 0;
      case 'boolean':
        return false;
      case 'array':
      case 'list':
        return [];
      case 'object':
        return {};
      case 'file[]':
      case 'file':
      case 'image':
        return [];
      default:
        return '';
    }
  };

  // Specialized component renderers
  const renderTextField = (field, value, onChange, hasError, fieldErrors, showLabel = true, isDisabled = false) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const maxLength = field.maxLength || 255;

    return (
      <TextField
        fullWidth
        label={showLabel ? (
          fieldRequired ? (
            <span>
              <span style={{ color: '#f44336' }}>*</span> {fieldLabel}
            </span>
          ) : fieldLabel
        ) : undefined}
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : fieldDescription}
        required={fieldRequired}
        disabled={readOnly || isDisabled}
        inputProps={{
          maxLength: maxLength,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="text.secondary">
                {(value || '').length}/{maxLength}
              </Typography>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const renderTextarea = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const maxLength = field.maxLength || 2000;
    const rows = field.rows || 4;

    return (
      <TextField
        fullWidth
        label={fieldLabel}
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : fieldDescription}
        required={fieldRequired}
        disabled={readOnly}
        multiline
        rows={rows}
        inputProps={{
          maxLength: maxLength,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="text.secondary">
                {(value || '').length}/{maxLength}
              </Typography>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const renderNumberInput = (field, value, onChange, hasError, fieldErrors, showLabel = true, isDisabled = false) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const min = field.min || 0;
    const max = field.max;
    const step = field.step || (field.Type === 'integer' ? 1 : 0.01);

    return (
      <TextField
        fullWidth
        label={showLabel ? (
          fieldRequired ? (
            <span>
              <span style={{ color: '#f44336' }}>*</span> {fieldLabel}
            </span>
          ) : fieldLabel
        ) : undefined}
        type="number"
        value={value}
        onChange={(e) => onChange(fieldName, Number(e.target.value))}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : fieldDescription}
        required={fieldRequired}
        disabled={readOnly || isDisabled}
        inputProps={{
          min: min,
          max: max,
          step: step,
        }}
        InputProps={{
          startAdornment: field.currency && (
            <InputAdornment position="start">₦</InputAdornment>
          ),
        }}
      />
    );
  };

  const renderToggleSwitch = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldDescription = field.Description || field.helpText;
    const labels = field.labels || ['Yes', 'No'];

    return (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={value}
              onChange={(e) => onChange(fieldName, e.target.checked)}
              disabled={readOnly}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">{fieldLabel}</Typography>
              <Chip
                label={value ? labels[0] : labels[1]}
                size="small"
                color={value ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          }
        />
        {fieldDescription && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {fieldDescription}
          </Typography>
        )}
        {hasError && (
          <FormHelperText error>{fieldErrors[0]}</FormHelperText>
        )}
      </Box>
    );
  };

  const renderSelect = (field, value, onChange, hasError, fieldErrors, showLabel = true, isDisabled = false) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    let fieldOptions = field.Options || field.enum || [];
    
    // Check if options are in Props.options format (e.g., [{label, value}])
    if (field.Props && field.Props.options) {
      fieldOptions = field.Props.options;
    }

    return (
      <FormControl fullWidth error={hasError} required={fieldRequired}>
        {showLabel && (
          <InputLabel>
            {fieldRequired ? (
              <span>
                <span style={{ color: '#f44336' }}>*</span> {fieldLabel}
              </span>
            ) : fieldLabel}
          </InputLabel>
        )}
        <Select
          value={value}
          onChange={(e) => onChange(fieldName, e.target.value)}
          disabled={readOnly || isDisabled}
          displayEmpty={!showLabel}
        >
          {!showLabel && (
            <MenuItem value="">
              <em>{fieldLabel}</em>
            </MenuItem>
          )}
          {fieldOptions.map((option) => {
            // Handle both string options and {label, value} object options
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            
            return (
              <MenuItem key={optionValue} value={optionValue}>
                {optionLabel}
              </MenuItem>
            );
          })}
        </Select>
        {hasError && <FormHelperText>{fieldErrors[0]}</FormHelperText>}
        {fieldDescription && <HtmlDescription description={fieldDescription} />}
      </FormControl>
    );
  };

  const renderMultiSelect = (field, value, onChange, hasError, fieldErrors, showLabel = true) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const fieldOptions = field.Options || field.enum || [];
    const fieldValue = value || [];

    return (
      <FormControl fullWidth error={hasError} required={fieldRequired}>
        {showLabel && (
          <InputLabel>
            {fieldRequired ? (
              <span>
                <span style={{ color: '#f44336' }}>*</span> {fieldLabel}
              </span>
            ) : fieldLabel}
          </InputLabel>
        )}
        <Select
          multiple
          value={fieldValue}
          onChange={(e) => onChange(fieldName, e.target.value)}
          disabled={readOnly}
          displayEmpty={!showLabel}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected || []).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {!showLabel && (
            <MenuItem value="">
              <em>{fieldLabel}</em>
            </MenuItem>
          )}
          {fieldOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {hasError && <FormHelperText>{fieldErrors[0]}</FormHelperText>}
        {fieldDescription && <HtmlDescription description={fieldDescription} />}
      </FormControl>
    );
  };

  // Render RadioGroup component with enhanced styling for badges
  const renderRadioGroup = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};
    let fieldOptions = fieldProps.options || field.Options || field.enum || [];

    // Convert string options to object format
    fieldOptions = fieldOptions.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return option;
    });

    const defaultValue = fieldProps.defaultValue !== undefined ? fieldProps.defaultValue : '';
    const currentValue = value !== undefined && value !== null ? value : defaultValue;

    return (
      <FormControl component="fieldset" error={hasError} required={fieldRequired} fullWidth>
        <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
          {fieldRequired && <span style={{ color: '#f44336' }}>* </span>}
          {fieldLabel}
        </FormLabel>
        
        <RadioGroup
          value={currentValue}
          onChange={(e) => onChange(fieldName, e.target.value)}
        >
          {fieldOptions.map((option, index) => (
            <Paper
              key={option.value}
              elevation={currentValue === option.value ? 3 : 1}
              sx={{
                p: 2,
                mb: 2,
                border: currentValue === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: currentValue === option.value ? '#f0f7ff' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  elevation: 2
                },
                position: 'relative'
              }}
              onClick={() => !readOnly && onChange(fieldName, option.value)}
            >
              <FormControlLabel
                value={option.value}
                control={<Radio disabled={readOnly} />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                      {option.badge && index === 0 && (
                        <Chip 
                          label={option.badge} 
                          size="small" 
                          color="success" 
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    {option.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ 
                  width: '100%', 
                  m: 0,
                  '& .MuiFormControlLabel-label': { width: '100%' }
                }}
              />
            </Paper>
          ))}
        </RadioGroup>

        {hasError && <FormHelperText error>{fieldErrors[0]}</FormHelperText>}
        {fieldDescription && <HtmlDescription description={fieldDescription} />}
      </FormControl>
    );
  };

  const renderDynamicList = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};
    const addButtonText = fieldProps.addButtonLabel || field.addButtonText || 'Add More';
    const subFields = fieldProps.fields || [];
    const minItems = fieldProps.minItems || 1;
    const maxItems = fieldProps.maxItems || 10;

    // Initialize with minimum required items
    const listValue = value || Array(minItems).fill().map(() => ({}));

    const addItem = () => {
      if (listValue.length < maxItems) {
        const newValue = [...listValue, {}];
        onChange(fieldName, newValue);
      }
    };

    const removeItem = (index) => {
      if (listValue.length > minItems) {
        const newValue = listValue.filter((_, i) => i !== index);
        onChange(fieldName, newValue);
      }
    };

    const handleItemChange = (index, subFieldName, subValue) => {
      const newValue = [...listValue];
      newValue[index] = { ...newValue[index], [subFieldName]: subValue };
      onChange(fieldName, newValue);
    };

    // If this is a Range Price Rules field, render special layout
    if (fieldName === 'Range Price Rules' || subFields.length > 0) {
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              {fieldLabel} {fieldRequired && '*'}
            </Typography>
            {!readOnly && listValue.length < maxItems && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addItem}
                size="small"
              >
                {addButtonText}
              </Button>
            )}
          </Box>

          {listValue.map((item, index) => (
            <Card key={index} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Range {index + 1}</Typography>
                {!readOnly && listValue.length > minItems && (
                  <IconButton onClick={() => removeItem(index)} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <Grid container spacing={2}>
                {subFields.map((subField) => {
                  const subFieldName = subField.Name || subField.name;
                  const subFieldLabel = subField.Label || subField.Name || subField.name;
                  const subFieldComponent = subField.Component || subField.component || 'TextField';
                  const subFieldSize = subField.Size || subField.size || { xs: 12 };
                  const subFieldValidation = subField.Validation || subField.validation || {};
                  const subFieldRequired = subFieldValidation.required || false;
                  
                  return (
                    <Grid size={subFieldSize} key={subFieldName}>
                      {(() => {
                        switch (subFieldComponent) {
                          case 'TextField': {
                            const subFieldProps = subField.Props || subField.props || {};
                            const subFieldShowLabel = subFieldProps.showLabel !== undefined ? subFieldProps.showLabel : true;
                            return renderTextField(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, [], subFieldShowLabel);
                          }
                          case 'NumberField': case 'NumberInput': {
                            const subFieldProps = subField.Props || subField.props || {};
                            const subFieldShowLabel = subFieldProps.showLabel !== undefined ? subFieldProps.showLabel : true;
                            return renderNumberInput(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, [], subFieldShowLabel);
                          }
                          case 'Select': {
                            const subFieldProps = subField.Props || subField.props || {};
                            const subFieldShowLabel = subFieldProps.showLabel !== undefined ? subFieldProps.showLabel : true;
                            return renderSelect(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, [], subFieldShowLabel);
                          }
                          case 'MultiSelect': {
                            const subFieldProps = subField.Props || subField.props || {};
                            const subFieldShowLabel = subFieldProps.showLabel !== undefined ? subFieldProps.showLabel : true;
                            return renderMultiSelect(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, [], subFieldShowLabel);
                          }
                          case 'SearchableSelect': {
                            return renderSearchableSelect(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, []);
                          }
                          case 'CustomNumberInput': {
                            return renderCustomNumberInput(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, []);
                          }
                          case 'Tiptap':
                          case 'Quill': return renderRichTextEditor(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, []);
                          default: return renderTextField(subField, item[subFieldName], (name, val) => handleItemChange(index, name, val), false, [], false, false);
                        }
                      })()}
                    </Grid>
                  );
                })}
              </Grid>
            </Card>
          ))}
          
          {fieldDescription && (
            <HtmlDescription description={fieldDescription} />
          )}
          
          {/* Special info alert for Bulk Pricing Tiers */}
          {(fieldName === 'Bulk Pricing Tiers' || fieldName === 'Range Price Rules') && listValue.length > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>💡 Bulk Pricing Impact:</strong> All product variants will automatically be set to{' '}
                <strong>₦{parseFloat(listValue[0]['Price (₦)'] || 0).toLocaleString()}</strong>{' '}
                (your Tier 1 base price). Bulk discounts apply based on total quantity purchased, regardless of variant selection.
              </Typography>
            </Alert>
          )}
          
          {hasError && (
            <FormHelperText error>{fieldErrors[0]}</FormHelperText>
          )}
        </Box>
      );
    }

    // Default simple list rendering for backward compatibility
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            {fieldLabel} {fieldRequired && '*'}
          </Typography>
          {!readOnly && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addItem}
              size="small"
            >
              {addButtonText}
            </Button>
          )}
        </Box>

        {listValue.map((item, index) => (
          <Paper key={index} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              fullWidth
              value={typeof item === 'string' ? item : ''}
              onChange={(e) => {
                const newValue = [...listValue];
                newValue[index] = e.target.value;
                onChange(fieldName, newValue);
              }}
              placeholder={`Item ${index + 1}`}
              disabled={readOnly}
            />
            {!readOnly && (
              <IconButton
                onClick={() => removeItem(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Paper>
        ))}

        {fieldDescription && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {fieldDescription}
          </Typography>
        )}
        {hasError && (
          <FormHelperText error>{fieldErrors[0]}</FormHelperText>
        )}
      </Box>
    );
  };

  // Render DynamicTable component for SKU pricing
  const renderDynamicTable = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};
    const columns = fieldProps.columns || [];
    const autoLoadFrom = fieldProps.autoLoadFrom || 'sku_combinations';

    // Get variants data for auto-loading SKU combinations
    const getVariantCombinations = () => {
      if (!variants || variants.length === 0) return [];
      
      const combinations = [];
      variants.forEach((variant, index) => {
        const variantTitle = variant['Variant Title'] || variant['variant_title'] || `Variant ${index + 1}`;
        const color = variant['Color'] || variant['color'] || '';
        const size = variant['Size / Capacity'] || variant['size_capacity'] || '';
        const price = variant['Price'] || variant['price'] || 0;
        const stock = variant['Stock Quantity'] || variant['stock_quantity'] || 0;
        
        const displayName = [variantTitle, color, size].filter(Boolean).join(' - ');
        
        combinations.push({
          variant: displayName,
          price: price,
          stock: stock,
          originalVariant: variant
        });
      });
      
      return combinations;
    };

    const tableValue = value || getVariantCombinations();

    const handleCellChange = (rowIndex, columnKey, newValue) => {
      const newTableValue = [...tableValue];
      
      // Find the column definition to check if it's a number type
      const column = columns.find(col => col.key === columnKey);
      const parsedValue = column && column.type === 'number' ? parseFloat(newValue) || 0 : newValue;
      
      newTableValue[rowIndex] = {
        ...newTableValue[rowIndex],
        [columnKey]: parsedValue
      };
      onChange(fieldName, newTableValue);
    };

    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {fieldLabel} {fieldRequired && '*'}
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableValue.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => {
                    const cellValue = row[column.key] || '';
                    const isEditable = column.editable !== false;
                    
                    return (
                      <TableCell key={column.key}>
                        {isEditable ? (
                          <TextField
                            type={column.type === 'number' ? 'number' : 'text'}
                            value={cellValue}
                            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
                            disabled={readOnly}
                            size="small"
                            fullWidth
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2">
                            {cellValue}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {fieldDescription && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {fieldDescription}
          </Typography>
        )}
        {hasError && (
          <FormHelperText error>{fieldErrors[0]}</FormHelperText>
        )}
      </Box>
    );
  };

  // Render SearchableSelect component
  const renderSearchableSelect = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Label || field.Name || field.name;
    const fieldDescription = field.Description || field.description;
    const fieldRequired = field.Validation?.required || field.validation?.required || field.Required || field.required || false;
    const fieldProps = field.Props || field.props || {};
    
    // Get options from various possible sources
    let fieldOptions = field.Options || field.options || field.enum || [];
    if (fieldProps.options) {
      fieldOptions = fieldProps.options;
    }
    
    // Convert options to proper format if they're strings
    const formattedOptions = fieldOptions.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return option;
    });

    // Component props
    const multiple = fieldProps.multiple || field.multiple || false;
    const allowCustomInput = fieldProps.allowCustomInput || field.allowCustomInput || false;
    const customInputLabel = fieldProps.customInputLabel || field.customInputLabel || 'Add custom option';
    const placeholder = fieldProps.placeholder || field.placeholder || (multiple ? 'Search and select multiple...' : 'Search and select...');
    const searchPlaceholder = fieldProps.searchPlaceholder || field.searchPlaceholder || 'Type to search...';
    const noOptionsText = fieldProps.noOptionsText || field.noOptionsText || 'No options found';
    const customInputPlaceholder = fieldProps.customInputPlaceholder || field.customInputPlaceholder || 'Enter custom value';
    const maxHeight = fieldProps.maxHeight || field.maxHeight || 300;
    const loading = fieldProps.loading || field.loading || false;

    return (
      <SearchableSelect
        label={fieldLabel}
        value={value}
        onChange={(newValue) => onChange(fieldName, newValue)}
        options={formattedOptions}
        multiple={multiple}
        allowCustomInput={allowCustomInput}
        customInputLabel={customInputLabel}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        noOptionsText={noOptionsText}
        customInputPlaceholder={customInputPlaceholder}
        maxHeight={maxHeight}
        loading={loading}
        required={fieldRequired}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : undefined}
        description={fieldDescription}
        disabled={readOnly}
        fullWidth
      />
    );
  };

  // Render CustomNumberInput component
  const renderCustomNumberInput = (field, value, onChange, hasError, fieldErrors, isDisabled = false) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Label || field.Name || field.name;
    const fieldDescription = field.Description || field.description;
    const fieldRequired = field.Validation?.required || field.validation?.required || field.Required || field.required || false;
    const fieldProps = field.Props || field.props || {};
    
    // Component props
    const allowNegativeValue = fieldProps.allowNegativeValue || field.allowNegativeValue || false;
    const placeholder = fieldProps.placeholder || field.placeholder || "Enter number...";
    const min = fieldProps.min || field.min || field.Validation?.min || field.validation?.min;
    const max = fieldProps.max || field.max || field.Validation?.max || field.validation?.max;
    const decimalPlaces = fieldProps.decimalPlaces || field.decimalPlaces;
    const suffix = fieldProps.suffix || field.suffix;
    const prefix = fieldProps.prefix || field.prefix;

    return (
      <CustomNumberInput
        label={fieldLabel}
        value={value}
        onChange={(newValue) => onChange(fieldName, newValue)}
        placeholder={placeholder}
        allowNegativeValue={allowNegativeValue}
        min={min}
        max={max}
        decimalPlaces={decimalPlaces}
        suffix={suffix}
        prefix={prefix}
        required={fieldRequired}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : undefined}
        description={fieldDescription}
        disabled={readOnly || isDisabled}
        fullWidth
      />
    );
  };

  const renderChipInput = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Label || field.Name || field.name;
    const fieldDescription = field.Description || field.description;
    const fieldRequired = field.Validation?.required || field.validation?.required || field.Required || field.required || false;
    const fieldProps = field.Props || field.props || {};
    
    const placeholder = fieldProps.placeholder || field.placeholder || "Type and press Enter to add...";
    const helperText = fieldProps.helperText || fieldDescription || '';

    return (
      <ChipInput
        label={fieldLabel}
        value={value}
        onChange={(newValue) => onChange(fieldName, newValue)}
        placeholder={placeholder}
        helperText={helperText}
        error={hasError}
        errorText={hasError ? fieldErrors[0] : ''}
        disabled={readOnly}
        fullWidth
      />
    );
  };

  const renderDynamicVariantFields = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldProps = field.Props || field.props || {};
    
    const sourceSection = fieldProps.sourceSection || 'Variant Attributes Definition';
    const sourceField = fieldProps.sourceField || 'Variant Attributes';
    const helperText = fieldProps.helperText || '';

    return (
      <DynamicVariantFields
        value={value}
        onChange={(newValue) => onChange(fieldName, newValue)}
        formData={formData}
        sourceSection={sourceSection}
        sourceField={sourceField}
        helperText={helperText}
        error={hasError}
        errorText={hasError ? fieldErrors[0] : ''}
        disabled={readOnly}
      />
    );
  };

  const renderDynamicAttributeFields = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldProps = field.Props || field.props || {};
    
    const sourceField = fieldProps.sourceField || 'Variant Attributes';
    const helperText = fieldProps.helperText || '';

    return (
      <DynamicAttributeFields
        value={value}
        onChange={(newValue) => onChange(fieldName, newValue)}
        formData={formData}
        sourceField={sourceField}
        helperText={helperText}
        error={hasError}
        errorText={hasError ? fieldErrors[0] : ''}
        disabled={readOnly}
      />
    );
  };

  const renderFileUploader = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const accept = field.accept || '*/*';
    const multiple = field.multiple || false;

    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      onChange(fieldName, multiple ? files : files[0]);
    };

    const removeFile = (index) => {
      if (multiple) {
        const newFiles = value.filter((_, i) => i !== index);
        onChange(fieldName, newFiles);
      } else {
        onChange(fieldName, null);
      }
    };

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {fieldLabel} {fieldRequired && '*'}
        </Typography>
        
        <Button
          variant="outlined"
          component="label"
          startIcon={<AttachFileIcon />}
          disabled={readOnly}
          sx={{ mb: 2 }}
        >
          Upload File
          <input
            type="file"
            hidden
            multiple={multiple}
            onChange={handleFileChange}
            accept={accept}
          />
        </Button>

        {value && (
          <Box>
            {multiple ? (
              value.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={!readOnly ? () => removeFile(index) : undefined}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))
            ) : (
              <Chip
                label={value.name}
                onDelete={!readOnly ? () => removeFile(0) : undefined}
                sx={{ mr: 1, mb: 1 }}
              />
            )}
          </Box>
        )}

        {fieldDescription && (
          <Typography variant="caption" color="text.secondary" display="block">
            {fieldDescription}
          </Typography>
        )}
        {hasError && (
          <FormHelperText error>{fieldErrors[0]}</FormHelperText>
        )}
      </Box>
    );
  };

  const renderImageUploader = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const maxFiles = field.maxFiles || 6;
    const minFiles = field.minFiles || 1;

    const handleImageChange = (e) => {
      const files = Array.from(e.target.files);
      const newFiles = [...(value || []), ...files].slice(0, maxFiles);
      onChange(fieldName, newFiles);
    };

    const removeImage = (index) => {
      const newImages = value.filter((_, i) => i !== index);
      onChange(fieldName, newImages);
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">
            {fieldLabel} {fieldRequired && '*'}
          </Typography>
          <Badge badgeContent={`${value?.length || 0}/${maxFiles}`} color="primary">
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCameraIcon />}
              disabled={readOnly || (value?.length || 0) >= maxFiles}
              size="small"
            >
              Add Images
              <input
                type="file"
                hidden
                multiple
                onChange={handleImageChange}
                accept="image/*"
              />
            </Button>
          </Badge>
        </Box>

        {value && value.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {value.map((file, index) => (
              <Paper
                key={index}
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Preview ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {!readOnly && (
                  <IconButton
                    size="small"
                    onClick={() => removeImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Paper>
            ))}
          </Box>
        )}

        {fieldDescription && (
          <Typography variant="caption" color="text.secondary" display="block">
            {fieldDescription}
          </Typography>
        )}
        {hasError && (
          <FormHelperText error>{fieldErrors[0]}</FormHelperText>
        )}
      </Box>
    );
  };

  const renderImageUpload = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const props = field.Props || {};
    
    const minFiles = props.minFiles || 4;
    const maxFiles = props.maxFiles || 20;
    const defaultSlots = props.defaultSlots || 4;
    const allowAddMore = props.allowAddMore !== false;
    const addMoreButtonLabel = props.addMoreButtonLabel || "Add More Images";
    const imageGuidelines = props.imageGuidelines || {};
    const slotLabels = props.slotLabels || {};
    
    const currentImages = value || [];
    // Calculate total slots based on images uploaded and default slots
    const totalSlots = Math.max(defaultSlots, currentImages.length);
    const canAddMore = allowAddMore && totalSlots < maxFiles;

    const handleImageChange = (e, slotIndex) => {
      const file = e.target.files[0];
      if (file) {
        const newImages = [...currentImages];
        newImages[slotIndex] = file;
        onChange(fieldName, newImages);
      }
    };

    const removeImage = (index) => {
      const newImages = currentImages.filter((_, i) => i !== index);
      onChange(fieldName, newImages);
    };

    const addMoreSlots = () => {
      // Add empty slots to trigger re-render with more slots
      const newImages = [...currentImages];
      // Add empty slots (4 at a time) up to maxFiles
      const slotsToAdd = Math.min(4, maxFiles - newImages.length);
      for (let i = 0; i < slotsToAdd; i++) {
        newImages.push(null);
      }
      onChange(fieldName, newImages);
    };

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {fieldLabel} {fieldRequired && '*'}
        </Typography>

        {/* Image Guidelines */}
        {imageGuidelines.description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {imageGuidelines.description}
          </Alert>
        )}

        {/* Image Upload Slots */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {Array.from({ length: totalSlots }, (_, index) => {
            const hasImage = currentImages[index] && currentImages[index] !== null;
            return (
              <Grid key={index} item xs={6} sm={4} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    border: '2px dashed',
                    borderColor: hasImage ? 'success.main' : 'grey.300',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => {
                    if (!readOnly) {
                      document.getElementById(`image-upload-${fieldName}-${index}`).click();
                    }
                  }}
                >
                  {hasImage ? (
                    <>
                      <ImageIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="caption" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {index === 0 ? (slotLabels.main || "Main Image") : (slotLabels.additional || "Image")}
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {currentImages[index].name}
                      </Typography>
                      {!readOnly && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                      <Typography variant="caption" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {index === 0 ? (slotLabels.main || "Main Image") : (slotLabels.additional || "Image")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to upload
                      </Typography>
                    </>
                  )}
                  
                  {!readOnly && (
                    <input
                      id={`image-upload-${fieldName}-${index}`}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, index)}
                    />
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Add More Button */}
        {canAddMore && !readOnly && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addMoreSlots}
              disabled={totalSlots >= maxFiles}
            >
              {addMoreButtonLabel}
            </Button>
          </Box>
        )}

        {/* File Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {currentImages.filter(img => img && img !== null).length} of {maxFiles} images uploaded
          </Typography>
          {currentImages.filter(img => img && img !== null).length < minFiles && (
            <Typography variant="caption" color="error">
              Minimum {minFiles} images required
            </Typography>
          )}
        </Box>

        {/* Description */}
        {fieldDescription && (
          <HtmlDescription description={fieldDescription} variant="caption" />
        )}

        {/* Error Display */}
        {hasError && (
          <FormHelperText error>
            {fieldErrors}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderVideoUpload = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const props = field.Props || {};
    
    const videoGuidelines = props.videoGuidelines || {};
    
    const currentVideo = value || null;

    const handleVideoChange = (video) => {
      onChange(fieldName, video);
    };

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {fieldLabel} {fieldRequired && '*'}
        </Typography>

        {/* Video Guidelines */}
        {videoGuidelines.description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {videoGuidelines.description}
          </Alert>
        )}

        {/* SingleVideoUpload Component (Admin - Preview only) */}
        <SingleVideoUpload
          value={currentVideo}
          onChange={handleVideoChange}
          onUpload={null}
          onDelete={null}
          productSid={null}
          merchantSid={null}
          mainImageUrl=""
          required={fieldRequired}
          disabled={readOnly}
        />

        {/* Description */}
        {fieldDescription && (
          <Box sx={{ mt: 2 }}>
            <HtmlDescription description={fieldDescription} variant="caption" />
          </Box>
        )}

        {/* Error Display */}
        {hasError && (
          <FormHelperText error>
            {fieldErrors}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderImageUploadDragDrop = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const props = field.Props || {};
    
    const minFiles = props.minFiles || 4;
    const maxFiles = props.maxFiles || 20;
    const maxFileSizeStr = props.maxFileSize || "2MB";
    const maxFileSize = parseInt(maxFileSizeStr) * 1024 * 1024; // Convert MB to bytes
    const imageGuidelines = props.imageGuidelines || {};
    const slotLabels = props.slotLabels || { main: 'Main Image', additional: 'Image' };
    const acceptedFileTypes = props.acceptedFileTypes || [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp'
    ];
    
    const currentImages = value || [];

    const handleImagesChange = (newImages) => {
      onChange(fieldName, newImages);
    };

    return (
      <Box>
        <ImageUploadDragDrop
          value={currentImages}
          onChange={handleImagesChange}
          minFiles={minFiles}
          maxFiles={maxFiles}
          maxFileSize={maxFileSize}
          acceptedTypes={acceptedFileTypes}
          label={fieldLabel + (fieldRequired ? ' *' : '')}
          helperText={fieldDescription || 'Drag and drop images here or click to browse'}
          error={hasError}
          errorMessage={fieldErrors}
          disabled={readOnly}
          imageGuidelines={imageGuidelines}
          slotLabels={slotLabels}
        />
      </Box>
    );
  };

  const renderSwitch = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const props = field.Props || {};
    
    const switchLabel = props.label || fieldLabel;
    const switchColor = props.color || 'primary';
    
    const handleSwitchChange = (event) => {
      onChange(fieldName, event.target.checked);
    };

    return (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={value || false}
              onChange={handleSwitchChange}
              color={switchColor}
              disabled={readOnly}
            />
          }
          label={
            <Box>
              <Typography variant="body1">
                {switchLabel} {fieldRequired && '*'}
              </Typography>
              {fieldDescription && (
                <HtmlDescription description={fieldDescription} variant="caption" />
              )}
            </Box>
          }
        />
        
        {hasError && (
          <FormHelperText error>
            {fieldErrors}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderDatePicker = (field, value, onChange, hasError, fieldErrors, isDisabled = false) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={fieldLabel}
          value={value}
          onChange={(newValue) => onChange(fieldName, newValue)}
          disabled={readOnly || isDisabled}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              error={hasError}
              helperText={hasError ? fieldErrors[0] : fieldDescription}
              required={fieldRequired}
            />
          )}
        />
      </LocalizationProvider>
    );
  };

  // Render Rich Text Editor (Quill)
  const renderRichTextEditor = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldRequired = field.Required || field.required;
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};
    const fieldValidation = field.Validation || field.validation || {};

    const handleEditorChange = (editorData) => {
      // Store both HTML and plain text, but primarily use HTML for rich content
      const valueToStore = editorData.html || editorData.text || '';
      onChange(fieldName, valueToStore);
    };

    return (
      <QuillEditor
        label={fieldLabel}
        value={value || ''}
        onChange={handleEditorChange}
        placeholder={fieldProps.placeholder || `Enter ${fieldLabel.toLowerCase()}...`}
        error={hasError}
        helperText={hasError ? fieldErrors[0] : fieldDescription}
        required={fieldRequired}
        disabled={readOnly}
        validation={fieldValidation}
        description={fieldDescription}
        {...fieldProps}
      />
    );
  };

  // Render Button
  const renderButton = (field, value, onChange, hasError, fieldErrors) => {
    const fieldName = field.Name || field.name;
    const fieldLabel = field.Name || field.label || field.name;
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};

    const handleButtonClick = () => {
      // Handle button click - for price preview, we'll show a modal
      if (fieldName === 'Preview Price Display') {
        handlePricePreview();
      }
    };

    const handlePricePreview = () => {
      // Get current form values - support both old and new field names
      const pricingMode = formData['Pricing Strategy'] || formData['Pricing Mode'];
      const bulkPrices = formData['Bulk Pricing Tiers'] || formData['Range Price Rules'] || [];
      const skuPrices = formData['SKU Price Rules'] || [];

      // Debug logging
      console.log('=== Price Preview Debug ===');
      console.log('Pricing Strategy:', pricingMode);
      console.log('Bulk Prices:', bulkPrices);
      console.log('SKU Prices:', skuPrices);
      console.log('Form Data:', formData);

      let previewData = null;
      
      if ((pricingMode === 'bulkPrice' || pricingMode === 'rangePrice') && bulkPrices.length > 0) {
        const baseTierPrice = parseFloat(bulkPrices[0]['Price (₦)']) || 0;
        
        // Validate that all variants have the same price as base tier
        const invalidVariants = variants.filter(v => {
          const variantPrice = parseFloat(v['Price'] || v.price || 0);
          return variantPrice !== baseTierPrice && variantPrice > 0;
        });
        
        if (invalidVariants.length > 0) {
          console.warn('⚠️ Variant price mismatch detected:', invalidVariants);
          previewData = {
            type: 'error',
            title: '⚠️ Price Validation Error',
            subtitle: `All variants must have the same price (₦${baseTierPrice.toLocaleString()}) when using Bulk Pricing mode. ${invalidVariants.length} variant(s) have different prices. Please refresh the page or adjust your pricing strategy.`
          };
        } else {
          const items = bulkPrices.map((price, index) => {
            const qty = price['Min Quantity'] || 1;
            const priceValue = price['Price (₦)'] || 0;
            const nextQty = bulkPrices[index + 1] ? bulkPrices[index + 1]['Min Quantity'] - 1 : '∞';
            
            // Calculate savings
            const savings = index > 0 ? (bulkPrices[0]['Price (₦)'] - priceValue) : 0;
            
            return {
              label: `${qty}${nextQty !== '∞' ? `-${nextQty}` : '+'} pieces`,
              value: `₦${priceValue.toLocaleString()}`,
              savings: savings > 0 ? `Save ₦${savings.toLocaleString()}!` : null
            };
          });
          previewData = {
            type: 'bulk',
            title: '💰 WHOLESALE PRICING',
            subtitle: 'Customers will see these quantity-based wholesale prices:',
            items,
            badge: 'WHOLESALE'
          };
        }
      } else if (pricingMode === 'skuPrice') {
        console.log('Processing SKU prices from variants...');
        console.log('Variants:', variants);
        
        // Read prices directly from variants array (no SKU Price Rules table)
        const variantPrices = variants.map(variant => {
          const priceVal = parseFloat(variant['Price'] || variant.price || 0);
          console.log('Variant:', variant, 'Price extracted:', priceVal);
          return {
            name: variant['Variant Title'] || variant['variant_title'] || 'Variant',
            price: priceVal
          };
        }).filter(v => !isNaN(v.price) && v.price > 0);
        
        console.log('Filtered variant prices:', variantPrices);
        
        if (variantPrices.length > 0) {
          const prices = variantPrices.map(v => v.price);
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          
          const items = variantPrices.map(v => ({
            label: v.name,
            value: `₦${v.price.toLocaleString()}`
          }));
          
          previewData = {
            type: 'sku',
            title: 'Per-Variant Price Display',
            subtitle: `Customers will see the price range:`,
            priceRange: minPrice === maxPrice ? `₦${minPrice.toLocaleString()}` : `₦${minPrice.toLocaleString()} - ₦${maxPrice.toLocaleString()}`,
            items
          };
        } else {
          console.log('No valid prices found in variants');
          previewData = {
            type: 'error',
            title: 'No Variant Prices Found',
            subtitle: 'Please add variants with prices in the Variants section below, then try again.'
          };
        }
      }
      
      if (!previewData) {
        previewData = {
          type: 'error',
          title: 'No Preview Available',
          subtitle: 'Please select a pricing mode and add price data to see the preview.'
        };
      }

      console.log('Final preview data:', previewData);
      setPricePreview(previewData);
    };

    return (
      <Box>
        <Button
          onClick={handleButtonClick}
          variant={fieldProps.variant || 'contained'}
          color={fieldProps.color || 'primary'}
          size={fieldProps.size || 'medium'}
          fullWidth={fieldProps.fullWidth || false}
          disabled={fieldProps.disabled || false}
          startIcon={fieldProps.startIcon === 'Visibility' ? '👁️' : undefined}
          sx={{ mb: 1 }}
        >
          {fieldLabel}
        </Button>
        {fieldDescription && <HtmlDescription description={fieldDescription} />}
        {hasError && <FormHelperText error>{fieldErrors[0]}</FormHelperText>}
        
        {/* Price Preview Display Box */}
        {fieldName === 'Preview Price Display' && pricePreview && (
          <Paper 
            elevation={3} 
            sx={{ 
              mt: 2, 
              p: 3, 
              backgroundColor: pricePreview.type === 'error' ? '#fff3e0' : (pricePreview.type === 'bulk' ? '#e3f2fd' : '#e8f5e9'),
              borderLeft: `4px solid ${pricePreview.type === 'error' ? '#ff9800' : (pricePreview.type === 'bulk' ? '#2196f3' : '#4caf50')}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {pricePreview.title}
              </Typography>
              {pricePreview.badge && (
                <Chip 
                  label={pricePreview.badge} 
                  size="small" 
                  color="primary" 
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {pricePreview.subtitle}
            </Typography>
            
            {pricePreview.type === 'sku' && pricePreview.priceRange && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {pricePreview.priceRange}
                </Typography>
              </Box>
            )}
            
            {pricePreview.items && pricePreview.items.length > 0 && (
              <Stack spacing={1}>
                {pricePreview.items.map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1.5,
                      backgroundColor: pricePreview.type === 'bulk' && index > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.7)',
                      borderRadius: 1,
                      border: '1px solid rgba(0, 0, 0, 0.08)'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                        {item.value}
                      </Typography>
                      {item.savings && (
                        <Chip 
                          label={item.savings} 
                          size="small" 
                          color="success" 
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                size="small" 
                onClick={() => setPricePreview(null)}
                sx={{ textTransform: 'none' }}
              >
                Close Preview
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  // Render Alert/Info Box
  const renderAlert = (field, value, onChange, hasError, fieldErrors) => {
    const fieldDescription = field.Description || field.helpText;
    const fieldProps = field.Props || field.props || {};
    const severity = fieldProps.severity || 'info';
    const variant = fieldProps.variant || 'standard';

    return (
      <Alert 
        severity={severity} 
        variant={variant}
        sx={{ mb: 2 }}
      >
        {fieldDescription && <HtmlDescription description={fieldDescription} />}
      </Alert>
    );
  };

  const renderDivider = (field) => {
    const fieldProps = field.Props || field.props || {};
    const subtitle = fieldProps.subtitle || '';
    const description = fieldProps.description || '';

    return (
      <Box sx={{ my: 3, width: '100%' }}>
        <Divider sx={{ mb: subtitle || description ? 2 : 0 }} />
        {subtitle && (
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 2, 
              mb: description ? 0.5 : 0,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            {subtitle}
          </Typography>
        )}
        {description && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mb: 1
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    );
  };

  // Validate field value
  const validateField = (field, value) => {
    const fieldErrors = [];

    // Required validation
    if (field.required && (!value || value === '')) {
      fieldErrors.push(`${field.label || field.name} is required`);
    }

    // Type-specific validation
    if (value !== '' && value !== null && value !== undefined) {
      switch (field.type) {
        case 'string':
        case 'text':
          // For Tiptap content, validate based on plain text length
          const textLength = field.Component === 'Tiptap' ? 
            value.replace(/<[^>]*>/g, '').trim().length : 
            value.length;
            
          if (field.minLength && textLength < field.minLength) {
            fieldErrors.push(`Must be at least ${field.minLength} characters`);
          }
          if (field.maxLength && textLength > field.maxLength) {
            fieldErrors.push(`Must be no more than ${field.maxLength} characters`);
          }
          break;
        case 'number':
        case 'integer':
          const numValue = Number(value);
          if (isNaN(numValue)) {
            fieldErrors.push('Must be a valid number');
          } else {
            if (field.min !== undefined && numValue < field.min) {
              fieldErrors.push(`Must be at least ${field.min}`);
            }
            if (field.max !== undefined && numValue > field.max) {
              fieldErrors.push(`Must be no more than ${field.max}`);
            }
          }
          break;
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            fieldErrors.push('Must be a valid email address');
          }
          break;
        case 'url':
          try {
            new URL(value);
          } catch {
            fieldErrors.push('Must be a valid URL');
          }
          break;
      }
    }

    return fieldErrors;
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);

    // Validate field - handle both old and new schema structures (support both cases)
    let field = null;
    const fields = schema.fields || schema.Fields;
    const sections = schema.sections || schema.Sections;
    
    if (fields) {
      // Old structure
      field = fields.find(f => (f.name || f.Name) === fieldName);
    } else if (sections) {
      // New structure - search through all sections
      for (const section of sections) {
        const sectionFields = section.fields || section.Fields;
        if (sectionFields) {
          field = sectionFields.find(f => (f.Name || f.name) === fieldName);
          if (field) break;
        }
      }
    }
    
    if (field) {
      const fieldErrors = validateField(field, value);
      setErrors(prev => ({
        ...prev,
        [fieldName]: fieldErrors
      }));
    }

    onChange(newFormData);
  };

  // Handle variant change
  const handleVariantChange = (variantIndex, fieldName, value) => {
    const newVariants = [...variants];
    if (!newVariants[variantIndex]) {
      newVariants[variantIndex] = {};
    }
    newVariants[variantIndex][fieldName] = value;
    setVariants(newVariants);

    const newFormData = { ...formData, variants: newVariants };
    setFormData(newFormData);
    onChange(newFormData);
  };

  // Add new variant
  const addVariant = () => {
    const newVariant = {};
    
    // Check for Variants in top-level schema or in Sections array (support both cases)
    let variantSchema = schema.Variants || schema.variants;
    const sections = schema.Sections || schema.sections;
    
    if (!variantSchema && sections) {
      const variantSection = sections.find(section => 
        (section.Section || section.section || section.Name || section.name || '').toLowerCase() === 'variants'
      );
      if (variantSection) {
        const fields = variantSection.Fields || variantSection.fields;
        variantSchema = { Fields: fields };
      }
    }
    
    if (variantSchema && variantSchema.Fields) {
      variantSchema.Fields.forEach(field => {
        const fieldName = field.Name || field.name;
        const fieldType = field.Type || field.type;
        newVariant[fieldName] = getDefaultValue(fieldType);
      });
    }
    setVariants([...variants, newVariant]);
  };

  // Remove variant
  const removeVariant = (index) => {
    // Prevent removing the last variant
    if (variants.length <= 1) {
      return;
    }
    
    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);
    const newFormData = { ...formData, variants: newVariants };
    setFormData(newFormData);
    onChange(newFormData);
  };

  // Check if field should be visible based on dynamic rules
  const isFieldVisible = (field) => {
    const fieldName = field.Name || field.name;
    const fieldVisibility = field.Visibility || field.visibility;
    
    // Check static visibility first
    if (fieldVisibility === 'hidden') {
      // Check if there's a dynamic rule that might show it (support both cases)
      const sections = schema.Sections || schema.sections;
      const hasDynamicRule = sections?.some(section => {
        const sectionFields = section.Fields || section.fields;
        return sectionFields?.some(f => {
          const dynamic = f.Dynamic || f.dynamic;
          if (dynamic?.onChange?.showFieldsWhen) {
            return Object.values(dynamic.onChange.showFieldsWhen).some(
              fields => fields.includes(fieldName)
            );
          }
          return false;
        });
      });
      
      if (!hasDynamicRule) {
        return false; // Static hidden with no dynamic rule
      }
    }
    
    // Check dynamic visibility rules
    let shouldShow = fieldVisibility !== 'hidden';
    
    // Look for fields that control this field's visibility (support both cases)
    const sections = schema.Sections || schema.sections;
    if (sections) {
      sections.forEach(section => {
        const sectionFields = section.Fields || section.fields;
        sectionFields?.forEach(controlField => {
          const dynamic = controlField.Dynamic || controlField.dynamic;
          if (dynamic?.onChange?.showFieldsWhen) {
            const controlFieldName = controlField.Name || controlField.name;
            const controlValue = formData[controlFieldName];
            
            // Check if current field is controlled by this field
            Object.entries(dynamic.onChange.showFieldsWhen).forEach(([triggerValue, fieldsToShow]) => {
              if (fieldsToShow.includes(fieldName)) {
                // This field is controlled - check if it should show
                if (controlValue === triggerValue) {
                  shouldShow = true;
                } else if (fieldVisibility === 'hidden') {
                  shouldShow = false;
                }
              }
            });
          }
        });
      });
    }
    
    return shouldShow;
  };

  // Check if field should be disabled based on dynamic rules
  const isFieldDisabled = (field, variantIndex = null) => {
    const dynamic = field.Dynamic || field.dynamic;
    if (!dynamic || !dynamic.enabledWhen) {
      return false; // Field is not conditionally disabled
    }

    const { field: dependentFieldName, condition } = dynamic.enabledWhen;
    
    // Get the value of the dependent field
    let dependentValue;
    if (variantIndex !== null) {
      // For variant fields, check the variant's field value
      dependentValue = variants[variantIndex]?.[dependentFieldName];
    } else {
      // For regular fields, check formData
      dependentValue = formData[dependentFieldName];
    }

    // Check the condition
    if (condition === 'hasValue') {
      // Field should be disabled if dependent field has no value
      return !dependentValue || dependentValue === '' || dependentValue === null || dependentValue === undefined;
    }

    return false;
  };

  // Render field based on component type
  const renderField = (field) => {
    const fieldName = field.Name || field.name;
    const fieldType = field.Type || field.type;
    const componentType = field.Component || field.component || fieldType;
    const fieldOptions = field.Options || field.enum;
    
    // Check dynamic visibility
    if (!isFieldVisible(field)) {
      return null;
    }
    
    const value = formData[fieldName] || getDefaultValue(fieldType);
    const fieldErrors = errors[fieldName] || [];
    const hasError = fieldErrors.length > 0;

    // Enhanced component-based mapping
    switch (componentType) {
      case 'TextField':
        const textFieldProps = field.Props || field.props || {};
        const textFieldShowLabel = textFieldProps.showLabel !== undefined ? textFieldProps.showLabel : true;
        return renderTextField(field, value, handleFieldChange, hasError, fieldErrors, textFieldShowLabel);

      case 'Textarea':
        return renderTextarea(field, value, handleFieldChange, hasError, fieldErrors);

      case 'NumberInput':
      case 'NumberField':
        const numberFieldProps = field.Props || field.props || {};
        const numberFieldShowLabel = numberFieldProps.showLabel !== undefined ? numberFieldProps.showLabel : true;
        return renderNumberInput(field, value, handleFieldChange, hasError, fieldErrors, numberFieldShowLabel);

      case 'ToggleSwitch':
        return renderToggleSwitch(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Select':
        const selectProps = field.Props || field.props || {};
        const selectShowLabel = selectProps.showLabel !== undefined ? selectProps.showLabel : true;
        return renderSelect(field, value, handleFieldChange, hasError, fieldErrors, selectShowLabel);

      case 'MultiSelect':
        const multiSelectProps = field.Props || field.props || {};
        const multiSelectShowLabel = multiSelectProps.showLabel !== undefined ? multiSelectProps.showLabel : true;
        return renderMultiSelect(field, value, handleFieldChange, hasError, fieldErrors, multiSelectShowLabel);

      case 'RadioGroup':
        return renderRadioGroup(field, value, handleFieldChange, hasError, fieldErrors);

      case 'DynamicList':
        return renderDynamicList(field, value, handleFieldChange, hasError, fieldErrors);

      case 'DynamicTable':
        return renderDynamicTable(field, value, handleFieldChange, hasError, fieldErrors);

      case 'FileUploader':
      case 'FileUpload':
        return renderFileUploader(field, value, handleFieldChange, hasError, fieldErrors);

      case 'ImageUploader':
        return renderImageUploader(field, value, handleFieldChange, hasError, fieldErrors);

      case 'ImageUpload':
        return renderImageUpload(field, value, handleFieldChange, hasError, fieldErrors);

      case 'ImageUploadDragDrop':
        return renderImageUploadDragDrop(field, value, handleFieldChange, hasError, fieldErrors);

      case 'VideoUpload':
        return renderVideoUpload(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Switch':
      case 'ToggleSwitch':
        return renderSwitch(field, value, handleFieldChange, hasError, fieldErrors);

      case 'DatePicker':
        return renderDatePicker(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Tiptap':
      case 'Quill':
        return renderRichTextEditor(field, value, handleFieldChange, hasError, fieldErrors);

      case 'SearchableSelect':
        return renderSearchableSelect(field, value, handleFieldChange, hasError, fieldErrors);

      case 'CustomNumberInput':
        return renderCustomNumberInput(field, value, handleFieldChange, hasError, fieldErrors);

      case 'ChipInput':
        return renderChipInput(field, value, handleFieldChange, hasError, fieldErrors);

      case 'DynamicVariantFields':
        return renderDynamicVariantFields(field, value, handleFieldChange, hasError, fieldErrors);

      case 'DynamicAttributeFields':
        return renderDynamicAttributeFields(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Button':
        return renderButton(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Alert':
        return renderAlert(field, value, handleFieldChange, hasError, fieldErrors);

      case 'Divider':
        return renderDivider(field);

      // Fallback to type-based mapping for backward compatibility
      case 'string':
        if (fieldOptions) {
          return renderSelect(field, value, handleFieldChange, hasError, fieldErrors);
        }
        return renderTextField(field, value, handleFieldChange, hasError, fieldErrors);

      case 'text':
        return renderTextarea(field, value, handleFieldChange, hasError, fieldErrors);

      case 'number':
      case 'integer':
        return renderNumberInput(field, value, handleFieldChange, hasError, fieldErrors);

      case 'boolean':
        return renderToggleSwitch(field, value, handleFieldChange, hasError, fieldErrors);

      case 'enum':
      case 'choice':
        return renderSelect(field, value, handleFieldChange, hasError, fieldErrors);

      case 'enum-multi':
        return renderMultiSelect(field, value, handleFieldChange, hasError, fieldErrors);

      case 'list':
        return renderDynamicList(field, value, handleFieldChange, hasError, fieldErrors);

      case 'file':
        return renderFileUploader(field, value, handleFieldChange, hasError, fieldErrors);

      case 'image':
      case 'file[]':
        return renderImageUploader(field, value, handleFieldChange, hasError, fieldErrors);

      case 'date':
        return renderDatePicker(field, value, handleFieldChange, hasError, fieldErrors);

      default:
        // Fallback to basic TextField for unknown types
        return (
          <TextField
            fullWidth
            label={field.Name || field.label || field.name}
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            error={hasError}
            helperText={hasError ? fieldErrors[0] : field.Description || field.helpText}
            required={field.Required || field.required}
            disabled={readOnly}
          />
        );
    }
  };

  // Render variant image upload with thumbnail preview
  const renderVariantImageUpload = (variantIndex, variantImage) => {
    const handleImageSelect = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, or WebP)');
        return;
      }
      
      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        alert('Image size must be less than 2MB');
        return;
      }
      
      // Create thumbnail preview
      const reader = new FileReader();
      reader.onloadend = () => {
        // Update variant with image file and preview
        handleVariantChange(variantIndex, 'image', file);
        handleVariantChange(variantIndex, 'imagePreview', reader.result);
      };
      reader.readAsDataURL(file);
    };
    
    const handleRemoveImage = () => {
      handleVariantChange(variantIndex, 'image', null);
      handleVariantChange(variantIndex, 'imagePreview', null);
    };
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Variant Image*
        </Typography>
        
        {variantImage ? (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <img 
              src={variantImage} 
              alt="Variant" 
              style={{ 
                width: 100, 
                height: 100, 
                objectFit: 'cover', 
                borderRadius: 4,
                border: '1px solid #ddd'
              }} 
            />
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'white',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={handleRemoveImage}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            size="small"
            sx={{ borderColor: 'error.main', color: 'error.main', '&:hover': { borderColor: 'error.dark' } }}
          >
            Upload Image
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageSelect}
            />
          </Button>
        )}
        
        <FormHelperText error>
          Required. Accepted formats: JPG, PNG, WebP (max 2MB).
        </FormHelperText>
      </Box>
    );
  };

  // Render variant fields
  const renderVariantFields = (variant, variantIndex) => {
    // Check for Variants in top-level schema or in Sections array (support both cases)
    let variantSchema = schema.Variants || schema.variants;
    
    const sections = schema.Sections || schema.sections;
    if (!variantSchema && sections) {
      const variantSection = sections.find(section => 
        (section.Section || section.section || section.Name || section.name || '').toLowerCase() === 'variants'
      );
      if (variantSection) {
        const fields = variantSection.Fields || variantSection.fields;
        variantSchema = { Fields: fields };
      }
    }
    
    if (!variantSchema || !variantSchema.Fields) return null;

    return (
      <Card key={variantIndex} sx={{ mb: 2 }}>
        <CardHeader
          title={`Variant ${variantIndex + 1}`}
          action={
            !readOnly && variants.length > 1 && (
              <IconButton
                onClick={() => removeVariant(variantIndex)}
                color="error"
                size="small"
                title="Remove variant (minimum 1 required)"
              >
                <DeleteIcon />
              </IconButton>
            )
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            {variantSchema.Fields.map((field) => {
              const fieldSize = field.Size || field.size || { xs: 12, sm: 6, md: 4 };
              const fieldName = field.Name || field.name;
              const fieldType = field.Type || field.type;
              const fieldLabel = field.Name || field.label || field.name;
              const fieldRequired = field.Required || field.required;
              const fieldDescription = field.Description || field.helpText;
              const fieldOptions = field.Options || field.enum;
              
              const variantValue = variant[fieldName] || getDefaultValue(fieldType);
              const fieldErrors = errors[`variant_${variantIndex}_${fieldName}`] || [];
              const hasError = fieldErrors.length > 0;

              // Create a variant-specific field object for the renderer
              const variantField = {
                ...field,
                Name: fieldName,
                Type: fieldType,
                Required: fieldRequired,
                Description: fieldDescription,
                Options: fieldOptions
              };

              // Create a variant-specific onChange handler
              const variantOnChange = (fieldName, value) => {
                handleVariantChange(variantIndex, fieldName, value);
              };

              // Use Component field if available, otherwise fall back to Type
              const componentType = field.Component || field.component || fieldType;
              
              // Check if this field should be disabled based on dynamic rules
              let fieldIsDisabled = isFieldDisabled(variantField, variantIndex);
              
              // Special case: Lock Price field when using bulk pricing mode
              const pricingStrategy = formData['Pricing Strategy'] || formData['Pricing Mode'];
              const bulkTiers = formData['Bulk Pricing Tiers'] || formData['Range Price Rules'] || [];
              const isBulkPricingMode = (pricingStrategy === 'bulkPrice' || pricingStrategy === 'rangePrice') && bulkTiers.length > 0;
              const isPriceField = fieldName === 'Price' || fieldName === 'price' || fieldLabel === 'Price' || fieldLabel === 'Price (₦)';
              
              if (isPriceField && isBulkPricingMode) {
                fieldIsDisabled = true;
                // Add helper text for locked price field
                const baseTierPrice = parseFloat(bulkTiers[0]['Price (₦)']) || 0;
                variantField.helperText = `🔒 Price locked to base bulk price (₦${baseTierPrice.toLocaleString()}). Change pricing mode to edit individual variant prices.`;
              }

              return (
                <Grid size={fieldSize} key={fieldName}>
                  {(() => {
                    // Enhanced component-based mapping (same as main renderField)
                    switch (componentType) {
                      case 'TextField': {
                        const textFieldProps = variantField.Props || variantField.props || {};
                        const textFieldShowLabel = textFieldProps.showLabel !== undefined ? textFieldProps.showLabel : true;
                        return renderTextField(variantField, variantValue, variantOnChange, hasError, fieldErrors, textFieldShowLabel, fieldIsDisabled);
                      }

                      case 'Textarea':
                        return renderTextarea(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'NumberInput':
                      case 'NumberField': {
                        const numberFieldProps = variantField.Props || variantField.props || {};
                        const numberFieldShowLabel = numberFieldProps.showLabel !== undefined ? numberFieldProps.showLabel : true;
                        return renderNumberInput(variantField, variantValue, variantOnChange, hasError, fieldErrors, numberFieldShowLabel, fieldIsDisabled);
                      }

                      case 'ToggleSwitch':
                        return renderToggleSwitch(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'Select': {
                        const selectProps = variantField.Props || variantField.props || {};
                        const selectShowLabel = selectProps.showLabel !== undefined ? selectProps.showLabel : true;
                        return renderSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors, selectShowLabel, fieldIsDisabled);
                      }

                      case 'MultiSelect': {
                        const multiSelectProps = variantField.Props || variantField.props || {};
                        const multiSelectShowLabel = multiSelectProps.showLabel !== undefined ? multiSelectProps.showLabel : true;
                        return renderMultiSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors, multiSelectShowLabel);
                      }

                      case 'SearchableSelect':
                        return renderSearchableSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'CustomNumberInput':
                        return renderCustomNumberInput(variantField, variantValue, variantOnChange, hasError, fieldErrors, fieldIsDisabled);

                      case 'ChipInput':
                        return renderChipInput(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'DynamicVariantFields':
                        return renderDynamicVariantFields(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'DynamicAttributeFields':
                        return renderDynamicAttributeFields(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'DynamicList':
                        return renderDynamicList(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'FileUploader':
                      case 'FileUpload':
                        return renderFileUploader(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'ImageUploader':
                        return renderImageUploader(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'DatePicker':
                        return renderDatePicker(variantField, variantValue, variantOnChange, hasError, fieldErrors, fieldIsDisabled);

                      case 'Tiptap':
                      case 'Quill':
                        return renderRichTextEditor(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      case 'Button':
                        return renderButton(variantField, variantValue, variantOnChange, hasError, fieldErrors);

                      // Fallback to type-based mapping
                      case 'string':
                        if (fieldOptions) {
                          return renderSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                        }
                        return renderTextField(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'text':
                        return renderTextarea(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'number':
                      case 'integer':
                        return renderNumberInput(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'boolean':
                        return renderToggleSwitch(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'enum':
                      case 'choice':
                        return renderSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'enum-multi':
                        return renderMultiSelect(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'list':
                        return renderDynamicList(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'file':
                        return renderFileUploader(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'image':
                      case 'file[]':
                        return renderImageUploader(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      case 'date':
                        return renderDatePicker(variantField, variantValue, variantOnChange, hasError, fieldErrors);
                      
                      default:
                        return (
                          <TextField
                            fullWidth
                            label={fieldLabel}
                            value={variantValue}
                            onChange={(e) => handleVariantChange(variantIndex, fieldName, e.target.value)}
                            error={hasError}
                            helperText={hasError ? fieldErrors[0] : fieldDescription}
                            required={fieldRequired}
                            disabled={readOnly}
                            multiline={fieldType === 'text'}
                            rows={field.rows || 3}
                          />
                        );
                    }
                  })()}
                </Grid>
              );
            })}
            
            {/* Variant Image Upload */}
            <Grid item xs={12}>
              {renderVariantImageUpload(variantIndex, variant.imagePreview)}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (!schema) {
    return (
      <Alert severity="warning">
        No schema provided
      </Alert>
    );
  }

  // Render a single section
  const renderSection = (section, sectionIndex) => {
    const sectionName = (section.Section || section.section || section.Name || section.name || '').toLowerCase();
    
    // Check if this is the Variants section
    if (sectionName === 'variants') {
      return renderVariantsSection(section, sectionIndex);
    }
    
    // Regular section rendering
    const sectionFields = section.Fields || section.fields;
    const sectionTitle = section.Section || section.section || section.Name || section.name || `Section ${sectionIndex + 1}`;
    const sectionDescription = section.Description || section.description;
    
    return (
      <Card key={sectionIndex} sx={{ mb: 3 }}>
        <CardHeader
          title={sectionTitle}
          subheader={sectionDescription}
        />
        <CardContent>
          <Grid container spacing={3}>
            {sectionFields && sectionFields.map((field) => {
              const fieldSize = field.Size || field.size || { xs: 12, sm: 6, md: 4 };
              return (
                <Grid size={fieldSize} key={field.Name || field.name}>
                  {renderField(field)}
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Render variants section with special UI
  const renderVariantsSection = (section, sectionIndex) => {
    const sectionTitle = section.Section || section.section || section.Name || section.name || 'Variants';
    const sectionDescription = section.Description || section.description;
    
    return (
      <Card key={sectionIndex} sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Typography variant="h6">
              {sectionTitle} ({variants.length})
              {variants.length === 1 && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (minimum 1 required)
                </Typography>
              )}
            </Typography>
          }
          subheader={sectionDescription}
        />
        <CardContent>
          {variants.map((variant, index) => renderVariantFields(variant, index))}

          {variants.length === 0 && (
            <Alert severity="info">
              No variants available. Variants will be automatically created when schema is loaded.
            </Alert>
          )}

          {/* Add Variant button at the bottom */}
          {!readOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                onClick={addVariant}
              >
                Add Variant
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Dynamic section rendering function
  const renderSections = () => {
    const sections = schema.Sections || schema.sections;
    const fields = schema.Fields || schema.fields;
    
    if (sections) {
      // New enhanced structure with sections - render all sections in order
      return sections.map((section, sectionIndex) => 
        renderSection(section, sectionIndex)
      );
    } else {
      // Legacy structure - render main fields and any additional field arrays
      const sectionsToRender = [];
      
      // Main Fields
      if (fields) {
        sectionsToRender.push(
          <Box key="main-fields" sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {fields.map((field) => {
                const fieldSize = field.Size || field.size || { xs: 12, sm: 6, md: 4 };
                return (
                  <Grid size={fieldSize} key={field.Name || field.name}>
                    {renderField(field)}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      }
      
      // Additional field arrays (dynamic)
      Object.keys(schema).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (Array.isArray(schema[key]) && lowerKey !== 'fields' && lowerKey !== 'sections') {
          sectionsToRender.push(
            <Box key={key} sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Typography>
              <Grid container spacing={3}>
                {schema[key].map((field) => {
                  const fieldSize = field.Size || field.size || { xs: 12, sm: 6, md: 4 };
                  return (
                    <Grid size={fieldSize} key={field.Name || field.name}>
                      {renderField(field)}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        }
      });
      
      return sectionsToRender;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {schema.Category || schema.category || schema.Title || schema.title || 'Dynamic Form'}
      </Typography>
      
      {(schema.Description || schema.description) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {schema.Description || schema.description}
        </Typography>
      )}

      {/* Render all sections dynamically (including Variants in order) */}
      {renderSections()}

      {/* Legacy: Render top-level Variants if not in Sections array */}
      {!(schema.Sections || schema.sections) && (schema.Variants || schema.variants) && (() => {
        const variantSchema = schema.Variants || schema.variants;
        const variantDescription = variantSchema?.Description;
        
        return (
          <Card sx={{ mt: 3 }}>
            <CardHeader
              title={
                <Typography variant="h6">
                  Variants ({variants.length})
                  {variants.length === 1 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (minimum 1 required)
                    </Typography>
                  )}
                </Typography>
              }
              subheader={variantDescription}
            />
            <CardContent>
              {variants.map((variant, index) => renderVariantFields(variant, index))}

              {variants.length === 0 && (
                <Alert severity="info">
                  No variants available. Variants will be automatically created when schema is loaded.
                </Alert>
              )}

              {/* Add Variant button at the bottom */}
              {!readOnly && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={addVariant}
                  >
                    Add Variant
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Submit Button */}
      {mode === 'edit' && !readOnly && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={() => onSubmit(formData)}
            disabled={Object.values(errors).some(errorArray => errorArray.length > 0)}
          >
            Submit
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DynamicFormBuilder;
